import { Helmet } from "react-helmet-async";
import { testimonials, aggregateRating as defaultAggregateRating } from "@/data/testimonials";
import { buildFullUrl } from "@/i18n/detectLanguage";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQPageJsonLdProps {
  faqs: FAQItem[];
}

/**
 * Renders FAQPage JSON-LD structured data.
 * @see https://schema.org/FAQPage
 */
export const FAQPageJsonLd = ({ faqs }: FAQPageJsonLdProps) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface ProductJsonLdProps {
  name: string;
  description: string;
  price: string;
  currency?: string;
  image?: string;
  url?: string;
  brand?: string;
  category?: string;
  /** Pass true on pages that display testimonials visibly (enables AggregateRating for star snippets) */
  withRatings?: boolean;
  /**
   * The full tier catalogue, for pages that present every membership rather
   * than one. Emits an AggregateOffer spanning the price range with a nested
   * Offer per tier, so price queries across the range ("thailand elite visa
   * cost", "20 year thailand visa price") can match. Without it the page
   * advertises a single price, which on the homepage understated the
   * catalogue as 650,000 THB.
   */
  offerTiers?: { name: string; price: number; url: string }[];
}

/**
 * Renders Product JSON-LD structured data with optional AggregateRating.
 * NOTE: Google only shows stars for Product schema when reviews are VISIBLE on the page.
 * Set withRatings=true only on pages that render <TestimonialsSection />.
 * @see https://schema.org/Product
 */
export const ProductJsonLd = ({
  name,
  description,
  price,
  currency = "THB",
  image,
  url,
  brand = "Thailand Elite",
  category = "Visa & Membership Program",
  withRatings = false,
  offerTiers,
}: ProductJsonLdProps) => {
  const numericPrice = price.replace(/[^0-9]/g, "");
  const seller = {
    "@type": "Organization",
    name: "Thailand Privilege Card Company Limited",
  };

  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    brand: {
      "@type": "Brand",
      name: brand,
    },
    category,
    offers:
      offerTiers && offerTiers.length > 0
        ? {
            "@type": "AggregateOffer",
            priceCurrency: currency,
            lowPrice: Math.min(...offerTiers.map((tier) => tier.price)),
            highPrice: Math.max(...offerTiers.map((tier) => tier.price)),
            offerCount: offerTiers.length,
            availability: "https://schema.org/InStock",
            offers: offerTiers.map((tier) => ({
              "@type": "Offer",
              name: tier.name,
              price: tier.price,
              priceCurrency: currency,
              availability: "https://schema.org/InStock",
              url: tier.url,
              seller,
            })),
          }
        : {
            "@type": "Offer",
            price: numericPrice,
            priceCurrency: currency,
            availability: "https://schema.org/InStock",
            seller,
          },
  };

  if (image) data.image = image;
  if (url) data.url = url;

  // Only include aggregateRating when reviews are actually visible on the page.
  // Google will NOT show stars if the reviews aren't readable by users.
  if (withRatings) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: defaultAggregateRating.ratingValue,
      bestRating: defaultAggregateRating.bestRating,
      worstRating: defaultAggregateRating.worstRating,
      reviewCount: defaultAggregateRating.reviewCount,
    };
    data.review = testimonials.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.ratingValue,
        bestRating: 5,
      },
      author: {
        "@type": "Person",
        name: r.author,
      },
      datePublished: r.datePublished,
      reviewBody: r.text,
    }));
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface ServiceJsonLdProps {
  name: string;
  description: string;
  price: string;
  currency?: string;
  url?: string;
  /** Schema.org serviceType, e.g. "Residency-by-membership program" */
  serviceType?: string;
}

/**
 * Renders Service JSON-LD structured data for a membership tier.
 * A Thailand Elite membership is semantically a Service (not a shippable
 * Product), so this carries the GSSA provider + license identifier that the
 * Product schema doesn't. Emitted alongside ProductJsonLd — Google reads
 * multiple JSON-LD blocks describing the same entity.
 * @see https://schema.org/Service
 */
export const ServiceJsonLd = ({
  name,
  description,
  price,
  currency = "THB",
  url,
  serviceType = "Long-stay visa membership",
}: ServiceJsonLdProps) => {
  const numericPrice = price.replace(/[^0-9]/g, "");

  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    serviceType,
    provider: {
      "@type": "Organization",
      name: "Thailand Elite GSSA",
      identifier: "SA21/012",
    },
    areaServed: {
      "@type": "Country",
      name: "Thailand",
    },
    offers: {
      "@type": "Offer",
      price: numericPrice,
      priceCurrency: currency,
      availability: "https://schema.org/InStock",
    },
  };

  if (url) data.url = url;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface ArticleJsonLdProps {
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  url?: string;
  lang?: string;
}

/**
 * Renders Article JSON-LD structured data.
 * @see https://schema.org/Article
 */
export const ArticleJsonLd = ({
  headline,
  description,
  datePublished,
  dateModified,
  author = "Thailand Elite GSSA",
  image,
  url,
  lang = "en",
}: ArticleJsonLdProps) => {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    datePublished,
    author: {
      "@type": "Organization",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: "Thailand Elite GSSA",
      url: buildFullUrl("/", lang),
    },
  };

  if (image) data.image = image;
  if (url) data.url = url;
  if (dateModified) data.dateModified = dateModified;
  data.inLanguage = lang === "fr" ? "fr-FR" : lang;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

/**
 * Renders Organization JSON-LD (WITHOUT aggregateRating).
 *
 * ⚠️  Google policy: Organization/LocalBusiness pages that self-host their own
 * reviews are INELIGIBLE for star rich snippets. Removed aggregateRating to
 * avoid potential structured data policy violations.
 * Use ProductJsonLd with withRatings=true on pages with visible reviews instead.
 *
 * @see https://developers.google.com/search/docs/appearance/structured-data/review-snippet#guidelines
 */
export const OrganizationJsonLd = () => {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    // Shared with the Organization block public/_worker.js injects at the edge.
    // Same @id + same name = one entity in the graph instead of two competing
    // ones, so answer engines cite the brand consistently.
    "@id": "https://thailand-elite.com/#organization",
    name: "Thailand Elite GSSA",
    alternateName: "Thailand Privilege Card GSSA",
    url: "https://thailand-elite.com",
    logo: {
      "@type": "ImageObject",
      // /logo-thailand-elite.png does not exist and 404s — this file does.
      url: "https://thailand-elite.com/images/logo-email-transparent.png",
      width: 400,
      height: 205,
    },
    description:
      "Official GSSA for Thailand Elite Visa — long-stay visa memberships with premium privileges for international residents.",
    foundingDate: "2015",
    sameAs: [
      "https://www.facebook.com/thailandeliteagent",
      "https://www.instagram.com/thailand.elite/",
      "https://www.youtube.com/@thailandelitevisas",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: [
        "English",
        "French",
        "German",
        "Japanese",
        "Chinese",
        "Italian",
        "Russian",
      ],
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

interface WebPageJsonLdProps {
  url: string;
  name: string;
  description: string;
  primaryImage: string;
}

/**
 * Renders WebPage JSON-LD with primaryImageOfPage.
 * Helps Google choose the right image for text result previews.
 * @see https://developers.google.com/search/docs/appearance/google-images
 */
export const WebPageJsonLd = ({
  url,
  name,
  description,
  primaryImage,
}: WebPageJsonLdProps) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url,
    name,
    description,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: primaryImage,
    },
    isPartOf: {
      "@type": "WebSite",
      name: "Thailand Elite GSSA",
      url: "https://thailand-elite.com",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};
